import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE = "peptideclinic.cart.v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE) || "[]");
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE, JSON.stringify(items));
  }, [items]);

  const add = (product, quantity = 1) => {
    setItems((cur) => {
      const existing = cur.find((i) => i.product_id === product.product_id);
      if (existing) {
        return cur.map((i) =>
          i.product_id === product.product_id
            ? { ...i, quantity: Math.min(20, i.quantity + quantity) }
            : i
        );
      }
      return [
        ...cur,
        {
          product_id: product.product_id,
          name: product.name,
          price: product.price,
          image_url: product.image_url,
          slug: product.slug,
          presentation: product.presentation,
          dose: product.dose,
          quantity,
        },
      ];
    });
  };

  const updateQty = (product_id, quantity) =>
    setItems((cur) =>
      cur
        .map((i) => (i.product_id === product_id ? { ...i, quantity } : i))
        .filter((i) => i.quantity > 0)
    );

  const remove = (product_id) =>
    setItems((cur) => cur.filter((i) => i.product_id !== product_id));

  const clear = () => setItems([]);

  const total = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  const value = useMemo(
    () => ({ items, add, updateQty, remove, clear, total, count }),
    [items, total, count]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
